import React from 'react';
import Bugsnag from '@bugsnag/js';
import BugsnagPluginReact from '@bugsnag/plugin-react';
import type { BugsnagErrorBoundary } from '@bugsnag/plugin-react';
import BugsnagPerformance from '@bugsnag/browser-performance';
import { UserClaims } from '@okta/okta-auth-js';
import { getViteAppBugsnagId } from '../Vite/getViteAppBugsnagId';
import { getViteAppEnvironment } from '../Vite/getViteAppEnvironment';
import { getNetworkInfo } from '../platform/getNetworkInfo';

export let ErrorBoundary: BugsnagErrorBoundary | undefined;

export const initializeBugsnag = (userInfo: UserClaims) => {
  Bugsnag.start({
    apiKey: getViteAppBugsnagId(),
    appVersion: __APP_VERSION__,
    plugins: [new BugsnagPluginReact()],
    releaseStage: getViteAppEnvironment(),
    maxBreadcrumbs: 100,
    maxEvents: 100,
    metadata: {
      audience: userInfo.userType,
      locations: userInfo.cfa_perms,
    },
    onError: event => {
      event.addMetadata('network', getNetworkInfo());
    },
  });

  BugsnagPerformance.start({
    apiKey: getViteAppBugsnagId(),
    appVersion: __APP_VERSION__,
    releaseStage: getViteAppEnvironment(),
    autoInstrumentFullPageLoads: true,
    autoInstrumentRouteChanges: true,
    autoInstrumentNetworkRequests: true,
  });

  Bugsnag.setUser(
    userInfo['cfa-login'] as string,
    userInfo.login as string,
    userInfo.name as string
  );

  ErrorBoundary = Bugsnag?.getPlugin('react')?.createErrorBoundary(React);
};

export const sendBug = (
  error: string,
  severity: 'info' | 'warning' | 'error',
  page?: string
) => {
  Bugsnag.notify(new Error(error), function (event) {
    //if (condition to not send bug) return false
    event.severity = severity;
    event.context = page;
  });
};

export const setupGlobalErrorHandlers = () => {
  // Only catch unhandled promise rejections (not ones we're already handling)
  window.addEventListener('unhandledrejection', event => {
    // Log to Bugsnag but do not redirect
    sendBug(
      event.reason?.message ||
        String(event.reason) ||
        'Unhandled promise rejection',
      'error',
      'unhandledRejection'
    );
    event.preventDefault();
  });

  // Only redirect for critical global JavaScript errors that break React
  window.addEventListener('error', event => {
    // Skip resource loading errors (images, scripts, etc.)
    if (event.target !== window) {
      console.warn('Resource loading error:', event);
      return;
    }

    const message = event.error?.message || event.message || '';

    // ResizeObserver loop errors are benign — they occur when layout
    // recalculations can't finish in a single animation frame (e.g. popovers).
    // Browsers fire them as global errors but they don't affect functionality.
    if (
      message.includes('ResizeObserver loop limit exceeded') ||
      message.includes(
        'ResizeObserver loop completed with undelivered notifications'
      )
    ) {
      return;
    }

    sendBug(message || 'Global JavaScript error', 'error', 'globalError');

    /** Only redirect for critical errors that would cause blank screen.
     *  We use window.location instead of navigate in case React is broken
     */
    window.location.href = '/error';

    return true;
  });
};
